import { Box, Stack, Typography } from "@mui/material";
import legioneMooneLogo from "Assets/Images/Images/legione-moone.svg";
import { Colors } from "Components/Layout/Themes/Colors";
import { BackToMooneButton } from "Components/Licenses/BackToMooneButton";
import { NoLicenseContent } from "Components/Licenses/NoLicenseContent";
import { OrderLicenseDialog } from "Components/Licenses/OrderLicenseDialog";
import { PendingLicenseContent } from "Components/Licenses/PendingLicenseContent";
import { useHunterLicenseGate } from "Hooks/useHunterLicenseGate";
import { useIsPhone } from "Hooks/useIsPhone";
import { Navigate } from "Navigation/Navigate";
import { Resources, useResource } from "Translations/Resources";
import { MS_TIME } from "Utils/TimeUtils";
import { appUrl } from "Utils/UrlUtils";
import { useEffect, useState } from "react";

const PAGE_RESOURCES = Resources.Hunter.LicensePaymentPending;

/**
 * Blocking screen for hunters without an active license, enforced globally by
 * AuthenticatedRoute. Fully self-contained — no other route is reachable while
 * blocked: ordering (incl. additional orders), cancelling an order, and payment
 * details all happen here. Polls the licenses query so access opens
 * automatically once the payment-verification job confirms the transfer.
 * "Back to Mo.one" switches to the user's Customer context.
 */
export const LicensePaymentPendingPage = () => {
  const [selectedLicenseID, setSelectedLicenseID] = useState<string>();
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const { t } = useResource();
  const isPhone = useIsPhone();
  const { state, pendingLicenses } = useHunterLicenseGate({
    staleTime: 0,
    refetchInterval: 30 * MS_TIME.second,
  });

  const hasNoLicense = state === "noLicense";
  const title = t(PAGE_RESOURCES[hasNoLicense ? "NoLicenseTitle" : "Title"]);

  useEffect(() => {
    document.title = `${title} | ${t(Resources.Common.AppName)}`;
  }, [title, t]);

  if (state === "active" || state === "notApplicable") {
    return <Navigate to={appUrl("dashboard")} replace />;
  }

  // Derived defensively: cancelling the selected order (or the list changing
  // under polling) falls back to the first remaining one without effect syncing.
  const selectedLicense =
    pendingLicenses.find(({ publicID }) => publicID === selectedLicenseID) ??
    pendingLicenses[0];

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      position="relative"
      p={3}
    >
      {/* Absolute so the logo stays out of the flow — the content centers
          within the full viewport, not in the space below the logo. */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        display="flex"
        justifyContent="center"
        p={3}
      >
        <Box
          component="img"
          src={legioneMooneLogo}
          alt="Legi.one Mo.one"
          width="100%"
          maxWidth={340}
        />
      </Box>
      {/* position + bgcolor paint the content above the absolute logo, so on
          tight viewports the content just covers it instead of clashing —
          keeps my="auto" centering exact without reserving logo space. */}
      <Stack
        gap={4}
        alignItems="center"
        textAlign="center"
        maxWidth={480}
        width="100%"
        my="auto"
        position="relative"
        bgcolor={Colors.Gray}
      >
        <Typography variant="h5">{title}</Typography>
        {hasNoLicense ? (
          <NoLicenseContent onOrderLicense={() => setIsOrderDialogOpen(true)} />
        ) : (
          <PendingLicenseContent
            pendingLicenses={pendingLicenses}
            selectedLicense={selectedLicense}
            onSelectLicense={setSelectedLicenseID}
            onOrderAnotherLicense={() => setIsOrderDialogOpen(true)}
          />
        )}
        {!isPhone && <BackToMooneButton />}
      </Stack>
      {isPhone && (
        <Box width="100%" maxWidth={480} pt={4}>
          <BackToMooneButton />
        </Box>
      )}
      <OrderLicenseDialog
        isOpen={isOrderDialogOpen}
        onClose={() => setIsOrderDialogOpen(false)}
        onOrderCreated={setSelectedLicenseID}
      />
    </Box>
  );
};
